import type { TransactionScheduleCallbacks } from "@hooks/view/useViewTableMeta";
import React, {
    DependencyList,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { Transaction } from "@common/types/transaction";
import { EagerLoaded } from "@common/types/base/orm";
import { useMobileView } from "@hooks/useMobileView";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useDownloadFinancialDocumentFile } from "@components/documents/FinancialDocumentDetails/useDownloadFinancialDocumentFile";
import { toBase64 } from "@helpers/file";
import {
    detachFinancialTransactionMatch,
    uploadTransactionReceipt,
} from "@lib/transactions";
import { StandardModal } from "@design-system/Modal/Modal";
import { DetailsAside } from "@design-system/DetailsAside/DetailsAside";
import { ReceiptUploadDropzone } from "../ReceiptUpload/ReceiptUploadDropzone";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import {
    invalidateTransactionDetailsQueries,
    useTransactionDetailsQuery,
} from "./useTransactionDetailsQuery";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsProps } from "./types";
import { TransactionDetails } from "./TransactionDetails";

interface TransactionDetailsContainerProps {
    id: number;
    onClose(): void;
    hideDetails?: boolean;
    hideFooter?: boolean;
    hideHistory?: boolean;
    hideCloseButton?: boolean;
    recomputeTopOffsetOn?: DependencyList;
    initialOpenPanels?: string[];
    scheduleCallbacks?: TransactionScheduleCallbacks;
}

export const TransactionDetailsContainer: React.FC<
    TransactionDetailsContainerProps
> = ({
    id,
    onClose,
    hideFooter,
    hideHistory,
    hideDetails,
    hideCloseButton,
    recomputeTopOffsetOn,
    initialOpenPanels,
    scheduleCallbacks,
}) => {
    const [uploading, setUploading] = useState(false);
    const { saveTransaction, upsertTransaction } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const [prevTransaction, setPrevTransaction] = useState<
        | EagerLoaded<
              Transaction,
              "transactionMatches" | "pairedTransfer" | "splits"
          >
        | undefined
    >(undefined);

    const { invalidate: invalidateSavings } = useBillingStatus();

    const { transaction, memoRequired, receiptRequired, origins } =
        useTransactionDetailsQuery(id);

    const financialDocument =
        transaction?.transactionMatches[0]?.financialDocument ?? null;
    const { download: downloadReceipt, isDownloading } =
        useDownloadFinancialDocumentFile(financialDocument);

    useEffect(() => {
        if (!transaction) {
            return;
        }
        setPrevTransaction(transaction);
    }, [transaction, prevTransaction]);

    const updateTransaction: TransactionDetailsProps["onUpdate"] = useCallback(
        async (
            payload: UpdateTransactionDto,
            displayedChangedEntityName?: string,
        ) => {
            if (transaction) {
                await saveTransaction(
                    transaction,
                    payload,
                    displayedChangedEntityName,
                );
            }
        },
        [transaction, saveTransaction],
    );

    const addReceipt: TransactionDetailsProps["onAddReceipt"] = useCallback(
        async (file) => {
            setUploading(true);

            const uploadReceipt = async () => {
                const receipt = await toBase64(file);

                await uploadTransactionReceipt(id, {
                    receipt,
                    contentType: file.type,
                    name: file.name,
                });

                await invalidateTransactionDetailsQueries();
                await invalidateSavings();
            };
            await uploadReceipt().finally(() => {
                setUploading(false);
            });
        },
        [id, invalidateSavings],
    );

    const removeReceipt: TransactionDetailsProps["onRemoveReceipt"] =
        useCallback(async () => {
            if (!transaction) {
                return;
            }

            const link = transaction?.transactionMatches[0];

            if (!link) {
                return;
            }

            await detachFinancialTransactionMatch(transaction.id, link.id);
            await invalidateTransactionDetailsQueries();
            await invalidateSavings();
        }, [transaction, invalidateSavings]);

    const isMobile = useMobileView();

    const contextValue = useMemo(
        () => ({
            transaction,
            isDownloadingReceipt: isDownloading,
            onDownloadReceipt: downloadReceipt,
            onRemoveReceipt: removeReceipt,
            uploadingReceipt: uploading,
            onAddReceipt: addReceipt,
            onUpdate: updateTransaction,
            setTransaction: upsertTransaction,
            memoRequired,
            receiptRequired,
            origins,
        }),
        [
            transaction,
            isDownloading,
            downloadReceipt,
            removeReceipt,
            uploading,
            addReceipt,
            updateTransaction,
            upsertTransaction,
            memoRequired,
            receiptRequired,
            origins,
        ],
    );

    const transactionDetailsBody = (
        <TransactionDetailsContext.Provider value={contextValue}>
            <ReceiptUploadDropzone onAddReceipt={addReceipt}>
                <TransactionDetails
                    onClose={onClose}
                    hideFooter={hideFooter}
                    hideHistory={hideHistory}
                    hideDetails={hideDetails}
                    hideCloseButton={hideCloseButton ?? isMobile}
                    initialOpenPanels={initialOpenPanels}
                    scheduleCallbacks={scheduleCallbacks}
                />
            </ReceiptUploadDropzone>
        </TransactionDetailsContext.Provider>
    );

    return (
        <>
            {isMobile ? (
                <StandardModal show onClose={onClose}>
                    {transactionDetailsBody}
                </StandardModal>
            ) : (
                <DetailsAside recomputeTopOffsetOn={recomputeTopOffsetOn}>
                    {transactionDetailsBody}
                </DetailsAside>
            )}
        </>
    );
};
